import { Component, computed, forwardRef, inject, input, signal, viewChild } from '@angular/core';
import { AbstractFormControl } from '../abstract-form-control';
import { CorporateGroupService, TenantFilterDto, TenantItemDto, TenantService } from '@artemis-software/wr-api';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { NbPopoverDirective } from '@nebular/theme';
import { watch } from '@/utils/watch';
import { firstValueFrom } from 'rxjs';

@Component({
  standalone: false,
  selector: 'wr-catalog-source-tenant-select',
  templateUrl: './catalog-source-tenant-select.component.html',
  styleUrls: ['./catalog-source-tenant-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CatalogSourceTenantSelectComponent),
      multi: true,
    },
  ],
})
export class CatalogSourceTenantSelectComponent extends AbstractFormControl<string | undefined> {

  readonly popover = viewChild(NbPopoverDirective);

  readonly tenantService = inject(TenantService);
  readonly corporateGroupService = inject(CorporateGroupService);

  readonly corporateGroupId = input<string | undefined>();
  readonly excludeTenantId = input<string | undefined>();

  readonly filterText = signal('');

  readonly memberTenantIds = asyncComputed(async () => {
    const groupId = this.corporateGroupId();
    if (!groupId) return [];
    const group = await firstValueFrom(this.corporateGroupService.findById(groupId));
    return group?.memberTenantIds ?? [];
  }, []);

  readonly allInternalTenants = asyncComputed(() =>
    this.tenantService.findAll({ type: TenantFilterDto.TypeEnum.Internal, managementView: true, size: 1000 }));

  readonly selectableTenants = computed(() => {
    const members = new Set(this.memberTenantIds() ?? []);
    return (this.allInternalTenants() ?? [])
      .filter(tenant => members.has(tenant.id))
      .filter(tenant => tenant.id !== this.excludeTenantId())
      // a tenant using a foreign catalog itself cannot be a source (not transitive)
      .filter(tenant => !tenant.catalogSourceTenantId);
  });

  readonly filteredTenants = computed(() => {
    const text = this.filterText().toLowerCase().trim();
    if (!text) return this.selectableTenants();
    return this.selectableTenants().filter(tenant => tenant.name.toLowerCase().includes(text));
  });

  constructor() {
    super();
    watch([this.allInternalTenants, this.valueSignal], () => {
      const found = (this.allInternalTenants() ?? []).find(tenant => tenant.id === this.value);
      if (found) {
        this.filterText.set(found.name);
      }
    });
  }

  override writeValue(id: string | undefined): void {
    super.writeValue(id);
    if (!id) {
      this.filterText.set('');
      return;
    }
    const found = (this.allInternalTenants() ?? []).find(tenant => tenant.id === id);
    if (found) {
      this.filterText.set(found.name);
    }
  }

  onSelectionChange(tenant: TenantItemDto): void {
    this.filterText.set(tenant.name);
    this.value = tenant.id;
    this.popover()?.hide();
  }

  onClear(): void {
    this.value = undefined;
    this.filterText.set('');
  }
}
